import { useEffect, useState, useRef, memo } from 'react'
import { Modal, Box, Stack, Center, Text, Progress, Button, Group, Divider } from '@mantine/core'
import { IconSparkles, IconAlertCircle, IconRefresh } from '@tabler/icons-react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import { ARC_HERO_WASH } from 'themes/ArcTheme'
import { generateProjectWithAI, pollAskAI, AIProjectRequest, AIProjectArea, formatDateForAPI } from '../../services/AIProjectService'
import { ProjectFormData, Topic } from '../../types'

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

const AILoadingIcon = styled.div`
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(139, 92, 246, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #8b5cf6;
  }
`

/**
 * arcmosaico.ai hero wash softened with a white veil so the panel text stays
 * readable (AR-797)
 */
const WashBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.4)),
    ${ARC_HERO_WASH};
  border-radius: 8px;
  pointer-events: none;
`

const textPulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
`

const PulsingText = styled.span`
  animation: ${textPulse} 2s ease-in-out infinite;
  display: inline-block;
`

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #ef4444;
  }
`

const ErrorBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(239, 68, 68, 0.05) 0%,
    rgba(239, 68, 68, 0.1) 50%,
    rgba(239, 68, 68, 0.05) 100%
  );
  border-radius: 8px;
  pointer-events: none;
`

/**
 * Progress bar uses an asymptotic curve that grows quickly at first
 * then slows down, never reaching 100% until the API confirms completion.
 * This gives users a sense of continuous progress without false completion.
 *
 * Formula: progress = MAX * (1 - e^(-elapsed / TIME_CONSTANT))
 * At 20s: ~60%, at 40s: ~80%, at 60s: ~85%. Caps at 90%.
 */
const PROGRESS_CAP_PERCENT = 90
const PROGRESS_SLOWDOWN_SECONDS = 20
const PROGRESS_UPDATE_MS = 500
const POLLING_INTERVAL_MS = 5000
/**
 * AR-848: generation averages ~1 min and its duration cannot be predicted, so
 * the steps run on a fixed 10s cadence instead of trying to track real
 * progress. Five steps reach the last one at 40s, and that last one then holds
 * for however long the API still takes — the script never restarts.
 */
const MESSAGE_ROTATION_MS = 10000

interface AILoadingModalProps {
  opened: boolean
  onClose: () => void
  onComplete: (projectId: string) => void
  formData: ProjectFormData
  areas: Topic[]
}

export const AILoadingModal = memo(({ opened, onClose, onComplete, formData, areas }: AILoadingModalProps) => {
  const { t } = useTranslation()
  const [progress, setProgress] = useState(0)
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
  const [hasError, setHasError] = useState(false)
  const apiCallMade = useRef(false)
  const mountedRef = useRef(true)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)

  const statusMessages = [
    t('arc_ai_step_starting'),
    t('arc_ai_step_researching'),
    t('arc_ai_step_generating_structure'),
    t('arc_ai_step_creating_lessons'),
    t('arc_ai_step_finalizing'),
  ]

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }

  const startPolling = (requestId: string) => {
    stopPolling()

    pollingRef.current = setInterval(async () => {
      try {
        const result = await pollAskAI(requestId)
        if (!mountedRef.current) return

        if (result.status === 'completed' && result.result?.project_id) {
          stopPolling()
          setProgress(100)

          onComplete(result.result.project_id)
        } else if (result.status === 'failed') {
          stopPolling()

          setHasError(true)
        }
      } catch {
        if (!mountedRef.current) return
        stopPolling()
        setHasError(true)
      }
    }, POLLING_INTERVAL_MS)
  }

  const callAIApi = async () => {
    setHasError(false)

    // Map selected topic IDs to area objects with id and name
    const selectedAreas: AIProjectArea[] = formData.topics
      .map(topicId => {
        const area = areas.find(a => a.value === topicId)
        return area ? { id: area.id || area.value, name: area.label } : null
      })
      .filter((area): area is AIProjectArea => area !== null)

    // Build payload in camelCase - axios interceptor converts to snake_case
    // gradeId → grade_id, userPrompt → user_prompt, etc.
    const payload: AIProjectRequest = {
      request: formData.description,
      gradeId: formData.gradeLevel || '',
      userPrompt: formData.description,
      weeks: formData.planning.weeks,
      daysPerWeek: formData.planning.daysPerWeek,
      dateStart: formatDateForAPI(formData.planning.startDate),
      duration: formData.planning.lessonDurationMinutes,
      areas: selectedAreas
    }

    try {
      const response = await generateProjectWithAI(payload)

      if (!response.success || !response.data?.id) {
        throw new Error(response.error || 'Failed to generate project')
      }

      // Inicia polling com o ID retornado
      startPolling(response.data.id)
    } catch {
      if (!mountedRef.current) return
      setHasError(true)
    }
  }

  // Efeito para fazer a chamada à API
  useEffect(() => {
    if (!opened || apiCallMade.current) return

    apiCallMade.current = true
    callAIApi()
  }, [opened])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stopPolling()
    }
  }, [])

  const handleRetry = () => {
    stopPolling()
    setProgress(0)
    setCurrentMessageIndex(0)
    callAIApi()
  }

  // Messages advance once and stop on the last one; progress grows
  // asymptotically to 90% (100% only on completion)
  useEffect(() => {
    if (!opened) {
      stopPolling()
      setProgress(0)
      setCurrentMessageIndex(0)
      setHasError(false)
      apiCallMade.current = false
      return
    }

    if (hasError) return

    const startTime = Date.now()
    const progressTimer = setInterval(() => {
      const secs = (Date.now() - startTime) / 1000
      setProgress(Math.min(PROGRESS_CAP_PERCENT * (1 - Math.exp(-secs / PROGRESS_SLOWDOWN_SECONDS)), PROGRESS_CAP_PERCENT))
    }, PROGRESS_UPDATE_MS)

    // Walk the steps once and stay on the last one. The counter lives here
    // rather than in the state updater so that clearing the timer is not a
    // side effect inside a function React may invoke twice.
    const lastStep = statusMessages.length - 1
    let step = 0
    const messageTimer = setInterval(() => {
      step += 1
      setCurrentMessageIndex(step)
      if (step >= lastStep) clearInterval(messageTimer)
    }, MESSAGE_ROTATION_MS)

    return () => {
      clearInterval(progressTimer)
      clearInterval(messageTimer)
    }
  }, [opened, hasError, statusMessages.length])

  const modalProps = {
    opened,
    onClose,
    centered: true,
    size: 'lg' as const,
    withCloseButton: false,
    closeOnClickOutside: false,
    closeOnEscape: false,
    padding: 'lg' as const,
  }

  if (hasError) {
    return (
      <Modal {...modalProps}>
        <Group spacing="xs">
          <IconSparkles size={20} color="#8b5cf6" />
          <Text size="lg" weight={600}>{t('arc_ai_generating_title')}</Text>
        </Group>
        <Divider my="md" />
        <Box sx={{ position: 'relative', minHeight: 300 }}>
          <ErrorBackground />
          <Stack spacing="xl" p="xl" align="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Center>
              <ErrorIcon>
                <IconAlertCircle size={40} />
              </ErrorIcon>
            </Center>
            <Text size="lg" weight={600} align="center">
              {t('arc_ai_error_title')}
            </Text>
            <Text size="sm" color="dimmed" align="center" maw={400}>
              {t('arc_ai_error_description')}
            </Text>
            <Group position="center">
              <Button variant="default" radius="xl" onClick={onClose}>
                {t('general_buttons_cancel')}
              </Button>
              <Button
                variant="filled"
                color="grape"
                radius="xl"
                leftIcon={<IconRefresh size={16} />}
                onClick={handleRetry}
              >
                {t('arc_ai_retry')}
              </Button>
            </Group>
          </Stack>
        </Box>
      </Modal>
    )
  }

  return (
    <Modal {...modalProps}>
      <Group spacing="xs">
        <IconSparkles size={20} color="#8b5cf6" />
        <Text size="lg" weight={600}>{t('arc_ai_generating_title')}</Text>
      </Group>
      <Divider my="md" />
      <Box sx={{ position: 'relative', minHeight: 300, borderRadius: 12, overflow: 'hidden' }}>
        <WashBackground />
        <Stack spacing="xl" p={32} align="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Center>
            <AILoadingIcon>
              <IconSparkles size={40} />
            </AILoadingIcon>
          </Center>
          <Text size="lg" weight={600} align="center">
            <PulsingText>{statusMessages[currentMessageIndex]}</PulsingText>
          </Text>
          <Text size="sm" color="dimmed" align="center">
            {t('arc_ai_please_wait')}
          </Text>
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <Progress
              value={progress}
              size="sm"
              radius="xl"
              // AI gradient bar (stripes dropped: Mantine stripes are a
              // background-image and would override the gradient)
              styles={{ bar: { backgroundImage: 'linear-gradient(90deg, #5B4FEB, #A855F7)' } }}
            />
          </Box>
        </Stack>
      </Box>
    </Modal>
  )
})